/**
 * Shared access-token helper for authenticated API calls.
 *
 * Reads the current Supabase session and returns an Authorization header.
 * Returns an empty object when there is no session, so public endpoints
 * still work and authenticated endpoints fail closed with a 401.
 *
 * TIMEOUT GUARD: supabase.auth.getSession() serializes on a navigator.locks
 * mutex shared across every open tab. With many tabs (or a wedged lock) the
 * call can stall indefinitely, and every screen awaiting it hangs on its
 * spinner BEFORE any network request starts (the observed "Loading Creator
 * Studio forever" defect). The token read is raced against a short deadline;
 * on timeout the request proceeds without the header and the endpoint fails
 * closed with a 401, so the page reaches its honest error/retry state instead
 * of spinning forever.
 */

import { supabase } from "@/integrations/supabase/client";

const SESSION_READ_TIMEOUT_MS = 4_000;

/** Transient contention retries: with many tabs, getSession() can THROW
 *  ("Lock ... was released because another request stole it") or time out on
 *  the shared navigator.locks mutex. A signed-in user must never be sent
 *  unauthenticated (surfacing as HTTP 401 on a signed-in screen) because of a
 *  momentary lock squabble, so the read is retried before failing closed. */
const RETRY_DELAYS_MS = [250, 750];

async function readSessionOnce() {
  return Promise.race([
    supabase.auth.getSession().then(({ data }) => data.session),
    new Promise<null>((_, reject) =>
      setTimeout(() => reject(new Error("session read timed out")), SESSION_READ_TIMEOUT_MS)),
  ]);
}

/**
 * Lock-free last resort: read the session Supabase persisted to localStorage
 * (key "sb-<ref>-auth-token") and use its access token while it is still
 * comfortably valid. navigator.locks contention can make getSession() throw
 * or stall even though a perfectly good signed-in session sits in storage;
 * a signed-in user must never see "Sign in again" because of a lock squabble.
 */
function persistedAccessToken(): string | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const stored = JSON.parse(raw) as { access_token?: string; expires_at?: number } | null;
      const token = stored?.access_token;
      const expiresAtMs = typeof stored?.expires_at === "number" ? stored.expires_at * 1000 : 0;
      // 30s safety margin: never send a token about to expire mid-request.
      if (typeof token === "string" && token.length > 0 && expiresAtMs > Date.now() + 30_000) {
        return token;
      }
    }
  } catch { /* unreadable persisted session; fail closed below */ }
  return null;
}

export async function authHeader(): Promise<Record<string, string>> {
  for (let attempt = 0; ; attempt++) {
    try {
      const session = await readSessionOnce();
      const token = session?.access_token ?? persistedAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (e) {
      // A TIMEOUT means the lock is wedged: retrying would stack more full
      // deadlines onto a stall that will not clear in milliseconds. Fall back
      // to the persisted token so the request still goes out authenticated
      // (~4s max, the original no-hang guarantee). Only FAST throws (the
      // "another request stole it" lock contention) are retried; they resolve
      // in milliseconds once the competing acquirer releases the lock.
      const timedOut = e instanceof Error && e.message === "session read timed out";
      if (timedOut || attempt >= RETRY_DELAYS_MS.length) {
        const token = persistedAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      }
      await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
}

/** JSON-request variant: the shared hardened token read plus Content-Type.
 *  The single replacement for the ad-hoc per-component authHeaders() copies
 *  that each re-implemented a raw, unguarded getSession() read. */
export async function authHeadersJson(): Promise<Record<string, string>> {
  return { "Content-Type": "application/json", ...(await authHeader()) };
}

const REFRESH_TIMEOUT_MS = 6_000;

/**
 * Authenticated fetch with self-healing on stale sessions.
 *
 * A device resuming from sleep can hold an expired access token: the request
 * goes out stale, the API answers 401, and the user (who IS signed in) was
 * told "your session needs a refresh, reload the page". That reload is the
 * machine's job. On a 401 this forces ONE token refresh and retries the
 * request; only if the retry also fails does the caller surface an error.
 * Signed-out users are unaffected (no token before, none after; the single
 * 401 propagates).
 */
export async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const baseHeaders = (init.headers ?? {}) as Record<string, string>;
  const first = await fetch(input, { ...init, headers: { ...baseHeaders, ...(await authHeader()) } });
  if (first.status !== 401) return first;

  try {
    await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("session refresh timed out")), REFRESH_TIMEOUT_MS)),
    ]);
  } catch { /* refresh unavailable; the retry below decides */ }

  const retryAuth = await authHeader();
  if (!retryAuth.Authorization) return first;
  return fetch(input, { ...init, headers: { ...baseHeaders, ...retryAuth } });
}
